/**
 * useDraftAutoSave — auto-saves form state to IndexedDB on every change (debounced).
 * Restores draft on mount if one exists.
 *
 * Usage:
 *   const { restored, clearDraft, draftAge } = useDraftAutoSave({
 *     formKey: `addExpense:${groupId}`,
 *     value: newExpense,
 *     onRestore: (saved) => setNewExpense(saved),
 *     enabled: showAddExpense, // only save while modal is open
 *   });
 *   // On successful submit: clearDraft();
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { saveDraft, loadDraft, clearDraft as idbClearDraft } from '../lib/idb';

const DEBOUNCE_MS = 500;

export default function useDraftAutoSave({ formKey, value, onRestore, enabled = true }) {
  const { user } = useAuth();
  const [restored, setRestored] = useState(false);
  const [draftAge, setDraftAge] = useState(null);
  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;

  // Restore on mount / when enabled flips on
  useEffect(() => {
    if (!enabled || !user?.id || !formKey) return;
    let cancelled = false;
    loadDraft(user.id, formKey).then((draft) => {
      if (cancelled) return;
      if (draft && draft.value && onRestore) {
        onRestore(draft.value);
        setRestored(true);
        setDraftAge(draft.updated_at);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, user?.id, formKey]);

  // Auto-save on value change (debounced)
  useEffect(() => {
    if (!enabled || !userIdRef.current || !formKey) return undefined;
    const t = setTimeout(() => {
      saveDraft(userIdRef.current, formKey, value).catch(() => { /* ignore */ });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [enabled, formKey, value]);

  const clearDraft = () => {
    if (!userIdRef.current || !formKey) return Promise.resolve();
    setRestored(false);
    setDraftAge(null);
    return idbClearDraft(userIdRef.current, formKey);
  };

  return { restored, clearDraft, draftAge };
}
